import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import apiService from "../../service/apiService";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import * as XLSX from "xlsx";
import { dateFormatter } from "../../utilities/utillFunction";
import { Calendar } from "primereact/calendar";
import moment from "moment";
import { superAdmin } from "../../lib/constants";
import StatCard from "./StatCard";
import { RTLContext } from "../../App";
import OrderDetailsComponent from "./OrderDetailsComponent";
import StatusBadge from "./StatusBadge";
import { designerAdmin } from "../../lib/constants";
import { Toast } from "primereact/toast";



const Icons = {
    order: "/icons/order.svg",
    payment: "/icons/payment.svg",
    clients: "/icons/clients.svg"
}

const OrderDecomposition = () => {
    const serviceId = localStorage.getItem("serviceId");
    const [orders, setOrders] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selVendor, setSelVendor] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [disableButton, setDisableButton] = useState(false);
    const [dateRange, setDateRange] = useState([new Date(moment().subtract(7, "days").format("YYYY-MM-DD")), new Date(moment().format("YYYY-MM-DD"))]);
    const [masterData, setMasterData] = useState({
        "totalClients": 0,
        "totalTransactions": 0,
        "balenceCollected": 0
    })
    const [filteredOrders, setFilterOrders] = useState([])

    const toast = useRef(null);
    const { openSidebar } = useContext(RTLContext);
    const filterOverlay = useRef(null);

    const isAdmin = useMemo(() => {
        let arr = JSON.parse(localStorage.getItem("permissions")) ?? [];
        if (arr.includes(superAdmin)) return true;
    }, []);

    // Filter orders based on search query
    useEffect(() => {
        if (!Array.isArray(orders)) {
            setFilterOrders([]);
            return;
        }
        if (!searchQuery.trim()) {
            setFilterOrders(orders);
            return;
        }
        const query = searchQuery.toLowerCase();
        const tempFilteredOrders = orders.filter(order =>
            order.customerInfo?.name?.toLowerCase().includes(query) ||
            order.customerInfo?.email?.toLowerCase().includes(query)
        );
        setFilterOrders(tempFilteredOrders);
    }, [orders, searchQuery]);


    const getData = async (fromDownload, DateRange) => {
        if (!fromDownload) setLoading(true)
        else {
            setDisableButton(true)
        }
        try {
            if (!serviceId) {
                console.error("Missing serviceId");
                return;
            }

            let email = localStorage.getItem("emailId");
            let orgId = localStorage.getItem("orgId");
            let nqpermissions = JSON.parse(localStorage.getItem("permissions")) || [];

            const isSuperAdmin = nqpermissions.includes(superAdmin);
            const isDesignerAdmin = nqpermissions.includes(designerAdmin);

            if (!DateRange || DateRange.length < 2) {
                console.log("===", DateRange)
                return
            }
            let st = moment(DateRange[0]).format("YYYY-MM-DD") + " 00:00:00";
            let ed = moment(DateRange[1]).format("YYYY-MM-DD") + " 23:59:59";

            let order_match = {
                created_at: { $gte: st, $lte: ed },
                lineItems: { $exists: true }
            };
            if (fromDownload) {
                const query = searchQuery.toLowerCase();
                order_match = {
                    ...order_match,
                    $or: [
                        { "customerInfo.name": { $regex: query, $options: "i" } },
                        { "customerInfo.email": { $regex: query, $options: "i" } }
                    ]
                }
            }

            if (isDesignerAdmin) {
                order_match["designerInfo.org_str"] = orgId;
            } else if (!isSuperAdmin) {
                order_match["designerID"] = email;
            }

            let order_q = {
                operation: "aggregate",
                aggregate: [
                    { $match: order_match }
                ]
            };

            // ✅ If download → expand lineItems
            if (fromDownload) {
                order_q.aggregate.push(
                    { $unwind: "$lineItems" },

                    {
                        $project: {
                            // -------------------------
                            // ORDER LEVEL DATA
                            // -------------------------
                            orderID: 1,
                            orderDate: "$created_at",
                            projectName: "$ProjectName",
                            currency: 1,
                            orderStatus: 1,
                            location: 1,
                            customerName: "$customerInfo.name",
                            customerEmail: "$customerInfo.email",
                            customerPhone: "$customerInfo.mobile",
                            billingAddress: "$shipAddress",
                            designerName: "$designerInfo.name",
                            designerEmail: "$designerInfo.email",
                            lineItemID: "$lineItems.lineItemID",
                            itemName: "$lineItems.itemName",
                            sku: "$lineItems.sku",
                            vendor: "$lineItems.vendor",
                            quantity: "$lineItems.quantity",
                            unitPrice: "$lineItems.unitPrice",
                            totalPrice: "$lineItems.totalPrice",
                            lineItemStatus: "$lineItems.status",
                            lastUpdate: "$lineItems.last_update",
                            paymentMethod: "$paymentInfo.method",
                            paymentStatus: "$paymentInfo.status"
                        }
                    }
                );
            } else {
                order_q.aggregate.push({
                    $project: {
                        orderStatus: 1,
                        "customerInfo.name": 1,
                        "customerInfo.email": 1,
                        location: 1,
                        ProjectName: 1,
                        orderStatus: 1,
                        created_at: 1,
                        "customerInfo.userId": 1,
                        lineItems: 1,
                        orderID: 1
                    }
                });
            }


            order_q.aggregate.push({ $sort: { created_at: -1 } });


            const result = await apiService.httpDBService(
                order_q,
                serviceId,
                "nq-customer-order-transaction"
            );

            if (!Array.isArray(result)) {
                console.error("Invalid API response:", result);
                return;
            }

            let userIdMap = {};
            let finalTotalPrice = 0;

            const res = result.map((item) => {
                const totalPrice = Array.isArray(item.lineItems)
                    ? item.lineItems.reduce((acc, cur) => acc + (cur.totalPrice || 0), 0)
                    : 0;

                const key = item?.customerInfo?.userId;
                if (key) userIdMap[key] = 1;

                finalTotalPrice += totalPrice;

                return {
                    ...item,
                    totalPrice,
                };
            });
            if (fromDownload) {
                return result
            }

            setMasterData({
                totalClients: Object.keys(userIdMap).length,
                totalTransactions: result.length,
                balenceCollected: finalTotalPrice
            });

            setOrders(res);

        } catch (error) {
            console.error("Order Fetch Error:", error?.response || error);
        }
        finally {
            if (!fromDownload) setLoading(false)
            else {
                setDisableButton(false)
            }
        }
    };


    const formatCurrency = (value) => {
        if (value == null || value === "") return "";
        return `$${Number(value).toFixed(2)}`;
    };

    const getTotalOrderAmount = (row) => {
        return formatCurrency(row.orderAmt);
    };

    const showOrderDetails = (row) => {
        const openSideBar = () => {
            openSidebar(<OrderDetailsComponent orderID={row["orderID"]} orderDetails={row} />)
        }
        return <p onClick={openSideBar} style={{ "cursor": "pointer", "fontWeight": "700", fontSize: "16px" }} className="font-bold text-lg p-2 ">view</p>
    }

    const getTotalPrice = (row) => {
        return row?.lineItems?.totalPrice != null ? formatCurrency(row.lineItems.totalPrice) : "";
    };

    const getWholeSalePrice = (row) => {
        return row?.lineItems?.wholesalePrice != null ? formatCurrency(row.lineItems.wholesalePrice) : "";
    };

    const getUnitPrice = (row) => {
        return row?.lineItems?.unitPrice != null ? formatCurrency(row.lineItems.unitPrice) : "";
    };

    const getMSRP = (row) => {
        return row?.lineItems?.msrp != null ? formatCurrency(row.lineItems.msrp) : "";
    };

    const getFileName = (row) => {
        if (row.location) {
            const fileName = row.location.split("/").pop();
            return fileName;
        }
        return "";
    };

    // orderDecompositionNew.js


    const flattenOrders = (orders = []) => {
        if (!Array.isArray(orders)) return [];

        return orders.flatMap((order) => {
            // If lineItems is missing or not an array → skip safely
            if (!Array.isArray(order?.lineItems)) return [];

            return order.lineItems
                .filter(item => item) // remove undefined/null items
                .map((item) => ({
                    // -------------------------
                    // ORDER LEVEL DATA
                    // -------------------------
                    orderID: order?.orderID ?? "",
                    orderDate: order?.created_at ?? "",
                    projectName: order?.ProjectName ?? "",
                    currency: order?.currency ?? "",
                    orderStatus: order?.orderStatus ?? "",

                    // -------------------------
                    // CUSTOMER INFO
                    // -------------------------
                    customerName: order?.customerInfo?.name ?? "",
                    customerEmail: order?.customerInfo?.email ?? "",
                    customerPhone: order?.customerInfo?.mobile ?? "",
                    billingAddress: order?.shipAddress ?? "",

                    // -------------------------
                    // DESIGNER INFO (if available)
                    // -------------------------
                    designerName: order?.designerInfo?.name ?? "",
                    designerEmail: order?.designerInfo?.email ?? "",

                    // -------------------------
                    // LINE ITEM DATA
                    // -------------------------
                    lineItemID: item?.lineItemID ?? "",
                    itemName: item?.itemName ?? "",
                    sku: item?.sku ?? "",
                    vendor: item?.vendor ?? "",
                    quantity: item?.quantity ?? 0,
                    unitPrice: item?.unitPrice ?? 0,
                    totalPrice: item?.totalPrice ?? 0,
                    lineItemStatus: item?.status ?? "",
                    lastUpdate: item?.last_update ?? "",

                    // -------------------------
                    // PAYMENT INFO (optional)
                    // -------------------------
                    paymentMethod: order?.paymentInfo?.method ?? "",
                    paymentStatus: order?.paymentInfo?.status ?? ""
                }));
        });
    };

    const onDownload = async () => {

        if (!filteredOrders?.length) {
            toast.current.show({
                severity: "warn",
                summary: "No Data",
                detail: "No Orders available to export.",
                life: 3000
            });
            return;
        }
        else if (disableButton) {
            toast.current.show({
                severity: "warn",
                summary: "Downalod",
                detail: "Please wait downlaod in process",
                life: 3000
            });
            return;
        }

        const dataToExport = await getData(true, dateRange);

        const Data = dataToExport.map((item) => ({
            "Order No.": item.orderID || "N/A",
            "Project Name": item.projectName || "N/A",
            "Order Date": dateFormatter(
                item.orderDate,
                "MMM DD hh:mm A",
                "YYYY-MM-DD HH:mm:ss"
            ),
            "Order Status": item.orderStatus || "N/A",

            "Customer Name": item.customerName || "N/A",
            "Customer Email": item.customerEmail || "N/A",
            "Customer Phone": item.customerPhone || "N/A",

            "Designer Name": item.designerName || "N/A",
            "Designer Email": item.designerEmail || "N/A",

            "Product Name": item.itemName || "N/A",
            "SKU": item.sku || "N/A",
            "Vendor": item.vendor || "N/A",

            "Quantity": item.quantity || 0,
            "Unit Price": `$${Number(item.unitPrice || 0).toFixed(2)}`,
            "Total Price": `$${Number(item.totalPrice || 0).toFixed(2)}`,
            "Currency": item.currency || "N/A",

            "Billing Address": item.billingAddress || "N/A",
            "Location (Quote File)": item.location || "N/A",

            "Last Updated": dateFormatter(
                item.lastUpdate,
                "MMM DD hh:mm A",
                "YYYY-MM-DD HH:mm:ss"
            )
        }));

        const filename = `order_details_${moment().format("YYYY-MM-DD")}.xlsx`;

        const ws = XLSX.utils.json_to_sheet(Data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Order Details");
        XLSX.writeFile(wb, filename);
    };


    const handleDateChange = (e) => {
        const [formattedStartDate, formattedEndDate] = e.value;
        const temp = [];
        if (formattedStartDate) temp.push(formattedStartDate);
        if (formattedEndDate) temp.push(formattedEndDate);

        setDateRange(temp);
    };

    useEffect(() => {
        if (selVendor || (dateRange && dateRange.length > 1)) {
            getData(false, dateRange);
        }
    }, [selVendor]);

    return (
        <div className="p-0 grid justify-content-center m-0" style={{ background: "#FBF9F3", padding: "20px" }}>
            <Toast ref={toast} />
            <div className="col-12 grid justify-content-between align-items-center flex-wrap">
                <div className="col-12 grid justify-content-between align-items-center" > <h2 className="m-0 item-Header order-header col-12 lg:col-5"
                    style={{ whiteSpace: "nowrap", fontFamily: "Inter" }}>Order List</h2>
                    <div className="grid align-items-center order-controls col-12 lg:col-7 gap-2 flex-wrap">
                        <div className="col-12 lg:col-6 mb-2 lg:mb-0">
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" />
                                <InputText
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search"
                                    className="w-full"
                                    style={{
                                        borderRadius: "20px",
                                        boxShadow: '0 6px 8px -4px rgba(0, 0, 0, 0.15)',
                                        border: "1px solid #ddd",
                                        background: "white",
                                        height: "40px",
                                        padding: "10px 12px 10px 40px",
                                        fontSize: "14px",
                                        fontFamily: "Inter"
                                    }}
                                />
                            </span>
                        </div>

                        <div className="col-12 lg:col-3 mb-2 lg:mb-0">
                            <Button
                                icon="pi pi-filter"
                                label="Filter"
                                onClick={(e) => filterOverlay.current.toggle(e)}
                                style={{
                                    background: "white",
                                    height: "40px",
                                    boxShadow: '0 6px 8px -4px rgba(0, 0, 0, 0.15)',
                                    border: "1px solid #ddd",
                                    color: "#3d3d3d",
                                    borderRadius: "20px",
                                    padding: "10px 20px",
                                    whiteSpace: "nowrap"
                                }}
                                className="p-button-outlined col-12 hide-label-laptop"
                            >

                            </Button>
                        </div>

                        <div className="col-12 lg:col-3 mb-2 lg:mb-0">
                            <Button
                                disabled={disableButton || !filteredOrders.length}
                                icon="pi pi-download"
                                label="Export"
                                onClick={onDownload}
                                style={{
                                    background: "white",
                                    height: "40px",
                                    boxShadow: '0 6px 8px -4px rgba(0, 0, 0, 0.15)',
                                    border: "1px solid #ddd",
                                    color: "#3d3d3d",
                                    borderRadius: "20px",
                                    padding: "10px 20px",
                                    whiteSpace: "nowrap"
                                }}
                                className="p-button-outlined col-12 hide-label-laptop"
                            />
                        </div>
                    </div>


                    {/* Filter Overlay Panel */}
                    <OverlayPanel ref={filterOverlay} className="lg:col-5 col-12">
                        <div className="p-5 grid ">

                            <Calendar
                                id="range"
                                value={dateRange}
                                placeholder="Select Date Range"
                                onChange={handleDateChange}
                                selectionMode="range"
                                readOnlyInput
                                className="col-12 text-sm md:text-base"
                                dateFormat="dd-mm-yy"
                                maxDate={new Date()}
                                inline
                            />


                            <div className="col-12 flex  mt-3 gap-2">
                                <Button
                                    label="Clear"
                                    className="p-button-text "
                                    onClick={() => {
                                        const newRange = [
                                            new Date(moment().subtract(7, "days").format("YYYY-MM-DD")),
                                            new Date(moment().format("YYYY-MM-DD"))
                                        ];
                                        setDateRange(newRange);
                                        getData(false, newRange);
                                    }}
                                />
                                <Button
                                    label="Apply"
                                    onClick={() => {
                                        filterOverlay.current.hide()
                                        getData(false, dateRange);
                                    }}
                                    style={{ backgroundColor: "#9AAB89", border: "none" }}
                                />
                            </div>
                        </div>
                    </OverlayPanel>
                </div>
                <div className="grid justify-content-between col-12 mt-2 align-items-stretch ">
                    <StatCard
                        icon={<img src={process.env.REACT_APP_IMAGE_STATIC_URL + Icons["clients"]} style={{ height: "50px", }} alt="clients" />}
                        label="Total Clients    "
                        value={masterData["totalClients"]}
                    />
                    <StatCard
                        icon={<img src={process.env.REACT_APP_IMAGE_STATIC_URL + Icons["order"]} style={{ height: "50px" }} alt="transactions" />}
                        label="Total Transactions"
                        value={masterData["totalTransactions"]}
                    />
                    <StatCard
                        icon={<img src={process.env.REACT_APP_IMAGE_STATIC_URL + Icons["payment"]} style={{ height: "50px" }} alt="sales" />}
                        label="Balance Collected"
                        value={`\$${masterData["balenceCollected"]}`}
                    />
                </div>
            </div>

            {/* Data Table */}
            <div className="col-12 mt-2 ">
                <DataTable
                    loading={loading}
                    value={filteredOrders}
                    className="w-full card-table"
                    rowClassName={() => 'card-row'}
                    paginator={true}
                    rows={10}
                    responsiveLayout="scroll"
                    emptyMessage="No orders found"
                >
                    <Column header="Order No." bodyClassName="text-lg p-2 " headerClassName="dataTableHeader" body={(row) => { return `#${row["orderID"].substring(0, 4)}`; }}></Column>
                    <Column header="Proposal Name" bodyClassName="text-lg p-2 " headerClassName="dataTableHeader" body={(row) => {
                        if (row.location) {
                            const fileName = row.location.split("/").pop();
                            return fileName;
                        }
                        return "";
                    }}></Column>
                    <Column header="Project Name" bodyClassName="text-lg p-2 " headerClassName="dataTableHeader" field="ProjectName"></Column>
                    <Column header="Customer Name" bodyClassName="text-lg p-2 " headerClassName="dataTableHeader" field="customerInfo.name"></Column>
                    <Column header="Order Date" bodyClassName="text-lg p-2 " headerClassName="dataTableHeader" body={(row) => dateFormatter(row.created_at, "MMM DD hh:mm A", "YYYY-MM-DD HH:mm:ss")}></Column>
                    <Column header="Total Price" bodyClassName="text-lg p-2 " headerClassName="dataTableHeader" body={(row) => {
                        return <p className="ml-2">{`\$ ${row["totalPrice"].toFixed(2)}`}</p>
                    }}></Column>
                    <Column
                        header="Status"
                        bodyClassName="text-lg  "
                        headerClassName="dataTableHeader"
                        body={(row) => <StatusBadge status={row["orderStatus"]} />}
                    />
                    <Column header="" bodyClassName="text-lg p-2 " headerClassName="dataTableHeader" body={showOrderDetails}></Column>
                </DataTable>
            </div>
        </div>
    );
};

export default OrderDecomposition;